import type { CSSProperties } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Link, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth.js';
import { queryClient } from './query-client.js';
import { Status } from './routes/status.js';
import { History } from './routes/history.js';
import { useReloadOnNewVersion } from './version-check.js';

const navLinkStyle = ({ isActive }: { isActive: boolean }): CSSProperties => ({
  color: isActive ? 'var(--loop-brand-600)' : 'var(--loop-neutral-700)',
  fontWeight: isActive ? 528 : 400,
  textDecoration: 'none',
  fontSize: '0.875rem',
  padding: '4px 8px',
  borderRadius: 'var(--loop-radius-xs)',
});

export function App() {
  const { idToken, email, isLoading, signIn, signOut } = useAuth();
  useReloadOnNewVersion();

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--loop-neutral-100)' }}>
        <div style={{ color: 'var(--loop-neutral-700)' }}>Loading...</div>
      </div>
    );
  }

  if (!idToken) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--loop-neutral-100)' }}>
        <div className="signin-card">
          <img src="/vinz.jpg" alt="" className="signin-logo" />
          <h1>Vinz</h1>
          <p className="signin-subtitle">Staging us-west-2 — who holds each service, and what is running on it</p>
          <button onClick={signIn} className="google-btn">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--loop-neutral-100)' }}>
      <header>
        <div className="header-brand">
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', textDecoration: 'none' }}>
            <img src="/vinz.jpg" alt="" className="header-logo" />
            <h1>Vinz</h1>
          </Link>
          <nav style={{ display: 'flex', gap: 'var(--loop-space-050)', marginLeft: 'var(--loop-space-300)' }}>
            <NavLink to="/" end style={navLinkStyle}>Status</NavLink>
            <NavLink to="/history" style={navLinkStyle}>History</NavLink>
          </nav>
        </div>
        <div id="who">
          <span className="who-email">{email}</span>
          <button onClick={signOut} className="btn-secondary signout-btn">
            <span aria-hidden="true">↵</span> Sign out
          </button>
        </div>
      </header>
      <main>
        <QueryClientProvider client={queryClient}>
          <Routes>
            <Route path="/" element={<Status />} />
            <Route path="/history" element={<History />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </QueryClientProvider>
      </main>
    </div>
  );
}
